import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../../redux/store";

import {
  listenToPublishedProducts,
  filterLocalProductsByCountry,
  setFilters,
} from "../../../../redux/storeProductSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../../redux/storeCartSlice";
import { fetchAllCommercePartners } from "../../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../../redux/storeProductRatingSlice";
import { logout } from "../../../../redux/authSlice";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../../../redux/notificationsSlice";
import { CurrencyContext } from "../../../../GLOBAL/CurrencyContext";
import appLogo from "../../../../assets/app/logo.png";

/* ⭐ CATEGORY SOURCE — the same collection StoreProducts saves with. */
import {
  DEPARTMENTS,
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, X, ChevronRight, ChevronLeft, Plus, Minus, Check,
  Heart, User, ShoppingBag, MapPin, Globe, AlertTriangle, LogOut,
  Home, Shirt, Star, LayoutGrid, Package, ArrowUpRight,
  Eye,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE
═══════════════════════════════════════════════════════════ */
const APPAREL_DEPT_ID: string =
  DEPARTMENTS.find((d) => /apparel|apparall?el|fashion|clothing/i.test(d.name))?.id ?? "08";

/** Flat-lay garment shots are usually on white; multiply drops the box.
 *  Flip to false if your sellers upload dark-background photography. */
const PHOTO_BLEND = true;

/** The only number this file decides on its own — when to say "x left".
 *  Everything else comes from what the seller saved. */
const LOW_STOCK_AT = 3;

/* ═══════════════════════════════════════════════════════════
   DESIGN
═══════════════════════════════════════════════════════════ */
const H = {
  paper:     "#FBF6EE",
  paperWarm: "#FFF9EF",
  card:      "#FFFFFF",
  ink:       "#1B0E06",
  inkSoft:   "#4A3627",
  muted:     "#7A6653",
  faint:     "#A6947F",
  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",
  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",
  green:     "#3F8A4C",
  rule:      "#EEDFC4",
  ruleSoft:  "#F3E6CE",
};

/* the showroom floor — same plinth as FurnitureStore */
const FLOOR = {
  wall:       "#FFFDF8",
  wallEdge:   "#FBF3E4",
  boardLight: "#F3E3C7",
  boardDark:  "#E9D5B2",
};

const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || H.red;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE — ancestry by walking parentId, never prefixes
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(key);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

/** Path with the department stripped — everything here is Apparel. */
const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).filter((cid) => cid !== APPAREL_DEPT_ID).map(catName).join(" › ");

const APPAREL_CATEGORIES: CategoryItem[] = childrenOf(APPAREL_DEPT_ID);

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — nothing is named here. Whatever the seller filled
   in on StoreProducts is what shows; whatever they left blank
   never appears, and no attribute is ever invented.
═══════════════════════════════════════════════════════════ */
const toAttributeCategoryId = (id?: string | number | null): string => {
  if (id === null || id === undefined || id === "") return "";
  const chain = chainIdsOf(id).map((cid) => CAT_MAP.get(cid)!).filter(Boolean);
  const anchor = [...chain].reverse().find((c) => c.level === "category" || c.level === "department");
  return anchor ? `0${anchor.id}` : "";
};

const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const key = String(categoryId);
  const hit = attrCache.get(key);
  if (hit) return hit;

  const chain = [...chainIdsOf(categoryId)].reverse();
  const keys = [toAttributeCategoryId(categoryId), ...chain.flatMap((c) => [`0${c}`, c])].filter(Boolean);

  let found: any[] = [];
  for (const k of keys) {
    try {
      const defs = attributesForCategory(k as any);
      if (defs && defs.length) { found = defs; break; }
    } catch { /* unknown key — keep walking up */ }
  }
  attrCache.set(key, found);
  return found;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

const savedRaw = (item: any, def: any) => item?.attributes?.[String(def.id)];

/** The option objects the seller actually picked — never the full catalogue. */
const savedOptionsFor = (item: any, def: any): any[] => {
  const raw = savedRaw(item, def);
  if (isEmptyValue(raw) || !Array.isArray(def?.options)) return [];
  const ids = (Array.isArray(raw) ? raw : [raw]).map(String);
  return def.options.filter((o: any) => ids.includes(String(o.id)));
};

const resolveAttrValue = (attr: any, value: any): string => {
  if (isEmptyValue(value)) return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

/** A choice only exists when the seller saved more than one option for it.
 *  One saved option is a fact about the garment, not a question for the buyer. */
const variantAttrsForItem = (item: any): any[] =>
  resolveAttributes(item?.categoryId).filter((def: any) => savedOptionsFor(item, def).length > 1);

const hasVariants = (item: any) => variantAttrsForItem(item).length > 0;

interface Detail { name: string; value: string }
/** Everything saved that isn't a buyer choice → the spec rows / card chips. */
const detailsOf = (item: any): Detail[] => {
  const variantIds = new Set(variantAttrsForItem(item).map((d: any) => String(d.id)));
  return resolveAttributes(item?.categoryId)
    .filter((def: any) => !variantIds.has(String(def.id)))
    .filter((def: any) => !isEmptyValue(savedRaw(item, def)))
    .map((def: any) => ({ name: def.name, value: resolveAttrValue(def, savedRaw(item, def)) }))
    .filter((d) => d.value);
};

/** Saved swatches, whatever the colour attribute happens to be called. */
const swatchesOf = (item: any): { id: string; name: string; hex?: string }[] => {
  const out: { id: string; name: string; hex?: string }[] = [];
  resolveAttributes(item?.categoryId)
    .filter((def: any) => def.inputType === "color")
    .forEach((def: any) => {
      savedOptionsFor(item, def).forEach((o: any) =>
        out.push({ id: String(o.id), name: o.name || o.standard || String(o.id), hex: o.hex })
      );
    });
  return out;
};

type SortKey = "default" | "price_asc" | "price_desc" | "newest" | "name_asc";
const isOutOfStock = (item: any) => item?.available === false || (item?.stock ?? 0) <= 0;

/** Only ever derived from the seller's own compareAtPrice. Never assumed. */
const discountOf = (item: any): number | null => {
  const was = item?.compareAtPrice;
  const now = item?.rate ?? 0;
  if (!was || was <= now) return null;
  return Math.round(((was - now) / was) * 100);
};

/* ═══════════════════════════════════════════════════════════
   LOCATION TIERS
═══════════════════════════════════════════════════════════ */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null =>
  country ? COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null : null;
const isLocalTier = (t: number) => t <= 1;
const isGlobalTier = (t: number) => t >= 2;
const TIER_LABEL = ["In your city", "In your country", "Ships from your region", "Ships worldwide"];

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Jost:wght@300;400;500;600;700&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-thumb { background: ${H.rule}; border-radius: 99px; }
input, button, select { font-family: inherit; }
.ap :focus-visible { outline: 1.5px solid ${H.red}; outline-offset: 3px; }

@keyframes apIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
@keyframes apSway { 0%,100% { transform: rotate(-1.2deg); } 50% { transform: rotate(1.2deg); } }
@keyframes apFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }

.ap { min-height:100vh; background:${H.paper}; color:${H.ink};
      font-family:'Jost',sans-serif; padding-bottom:96px; }
.ap-serif { font-family:'Cormorant Garamond', Georgia, serif; font-weight:400; }
.ap-caps { font-size:10px; letter-spacing:.28em; text-transform:uppercase; font-weight:500; }
.ap-num { font-variant-numeric: tabular-nums; }

/* ── masthead ── */
.ap-top { position:sticky; top:0; z-index:200; background:rgba(251,246,238,.92);
          backdrop-filter:blur(12px); border-bottom:1px solid ${H.rule}; }
.ap-hbtn { display:flex; align-items:center; gap:6px; background:none; border:none;
           color:${H.inkSoft}; font-size:12px; font-weight:400; letter-spacing:.06em;
           cursor:pointer; padding:7px 2px; position:relative; white-space:nowrap; flex-shrink:0; }
.ap-hbtn::after { content:''; position:absolute; left:0; right:0; bottom:2px; height:1px;
                  background:${H.ink}; transform:scaleX(0); transform-origin:left;
                  transition:transform .28s cubic-bezier(.4,0,.2,1); }
.ap-hbtn:hover::after { transform:scaleX(1); }

.ap-search { display:flex; align-items:center; flex:1; min-width:0; gap:8px;
             border-bottom:1px solid ${H.rule}; padding:6px 2px; transition:border-color .25s; }
.ap-search:focus-within { border-color:${H.ink}; }
.ap-search input { flex:1; min-width:0; background:none; border:none; outline:none;
                   font-size:13px; color:${H.ink}; letter-spacing:.02em; }
.ap-search input::placeholder { color:${H.faint}; }

/* ── 🏷 THE HANGER RAIL ── */
.ap-rail { position:relative; padding-top:16px; }
.ap-rail-bar { position:absolute; top:15px; left:0; right:0; height:1px;
  background:linear-gradient(90deg, transparent, ${H.rule} 4%, ${H.rule} 96%, transparent); }
.ap-tags { display:flex; gap:12px; overflow-x:auto; scrollbar-width:none; padding:0 2px 20px; }
.ap-tag { position:relative; flex-shrink:0; padding-top:14px; background:none; border:none;
          cursor:pointer; transform-origin:50% 0; transition:transform .22s cubic-bezier(.34,1.4,.64,1); }
.ap-tag::before { content:''; position:absolute; top:0; left:50%; width:1px; height:14px;
                  background:${H.rule}; }
.ap-tag:hover { animation:apSway 1.6s ease-in-out infinite; }
.ap-tag-body { position:relative; display:flex; align-items:center; gap:7px;
  padding:9px 15px 10px; border:1px solid ${H.rule}; background:${H.card};
  border-radius:3px 3px 11px 11px; font-size:11.5px; letter-spacing:.09em;
  text-transform:uppercase; color:${H.muted}; white-space:nowrap;
  transition:border-color .2s, background .2s, color .2s; }
.ap-tag-body::before { content:''; position:absolute; top:5px; left:50%; transform:translateX(-50%);
  width:5px; height:5px; border-radius:50%; border:1px solid ${H.rule}; background:${H.paper}; }
.ap-tag:hover .ap-tag-body { border-color:${H.gold}; color:${H.ink}; }
.ap-tag.on { animation:none; transform:translateY(5px); }
.ap-tag.on .ap-tag-body { background:${H.red}; border-color:${H.red}; color:#fff; }
.ap-tag.on .ap-tag-body::before { border-color:rgba(255,255,255,.65); background:${H.redDark}; }

/* ── seam nav ── */
.ap-seam { display:flex; gap:22px; overflow-x:auto; scrollbar-width:none;
           border-bottom:1px solid ${H.rule}; padding-bottom:1px; }
.ap-seam button { background:none; border:none; cursor:pointer; padding:9px 0 11px;
  font-size:12px; letter-spacing:.1em; text-transform:uppercase; color:${H.faint};
  position:relative; white-space:nowrap; transition:color .2s; }
.ap-seam button:hover { color:${H.ink}; }
.ap-seam button.on { color:${H.ink}; }
.ap-seam button.on::after { content:''; position:absolute; left:0; right:0; bottom:-1px;
  height:1.5px; background:${H.red}; }

/* ══════════════════════════════════════════════
   THE CARD — the plinth, lifted from FurnitureStore.
   Every garment stands in a showroom: a wall, a floor
   line, a contact shadow. The photo sits ON something.
══════════════════════════════════════════════ */
/* ══════════════════════════════════════════════
   THE CARD — Saloisa's editorial frame: one tall
   photo, the copy laid over it, a full-width bar
   underneath. Square corners, hairline border.
══════════════════════════════════════════════ */
.ap-card { background:${H.card}; border-radius:0; overflow:hidden; cursor:pointer;
  animation:apIn .38s ease both; transition:box-shadow .28s, transform .28s, border-color .28s;
  position:relative; border:1px solid ${H.ruleSoft}; display:flex; flex-direction:column; }
.ap-card:hover { box-shadow:0 12px 40px rgba(27,14,6,.12); transform:translateY(-4px);
  border-color:${H.rule}; }
.ap-card .ap-hover { opacity:0; transition:opacity .22s; }
.ap-card:hover .ap-hover { opacity:1; }

.ap-frame { width:100%; overflow:hidden; position:relative; aspect-ratio:3/4;
  background:${H.paperWarm}; }
.ap-frame img { width:100%; height:100%; object-fit:cover; display:block;
  transition:transform .45s cubic-bezier(.34,1.56,.64,1); }
.ap-card:hover .ap-frame img { transform:scale(1.07); }
.ap-frame .ap-veil { position:absolute; inset:0; pointer-events:none;
  background:linear-gradient(to top, rgba(27,14,6,.62) 0%, transparent 55%); }

.ap-round { width:36px; height:36px; border-radius:50%; background:rgba(255,255,255,.92);
  border:none; cursor:pointer; display:flex; align-items:center; justify-content:center;
  box-shadow:0 2px 8px rgba(27,14,6,.18); }

.ap-flag { position:absolute; top:12px; left:12px; z-index:2; color:#fff;
  font-size:9px; font-weight:700; padding:3px 8px; letter-spacing:.10em;
  text-transform:uppercase; }

.ap-price-tag { display:inline-flex; align-items:center; background:${H.ink}; color:#fff;
  padding:5px 14px; font-weight:800; letter-spacing:.04em; font-size:13px; }

.ap-cart-btn { display:flex; align-items:center; justify-content:center; gap:6px;
  width:100%; padding:11px 0; background:${H.ink}; color:#fff; border:none; cursor:pointer;
  font-size:11px; font-weight:700; letter-spacing:.14em; text-transform:uppercase;
  transition:background .18s; }
.ap-cart-btn:hover { background:${H.inkSoft}; }

.ap-chip { display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:700;
  color:${H.muted}; background:${H.paper}; border:1px solid ${H.rule};
  border-radius:999px; padding:3px 9px; white-space:nowrap; }
.ap-chip.hi { color:${H.goldDark}; background:${H.goldSoft}; border-color:${H.gold}; }

.ap-spec { display:grid; grid-template-columns:auto 1fr; gap:0; border:1px solid ${H.rule};
  border-radius:12px; overflow:hidden; }
.ap-spec dt { padding:8px 12px; font-size:11px; font-weight:800; color:${H.muted};
  background:${H.paper}; border-bottom:1px solid ${H.ruleSoft}; white-space:nowrap; }
.ap-spec dd { padding:8px 12px; font-size:11.5px; font-weight:600; color:${H.ink};
  border-bottom:1px solid ${H.ruleSoft}; }
.ap-spec dt:last-of-type, .ap-spec dd:last-of-type { border-bottom:none; }

.ap-plus { transition: transform .15s; }
.ap-plus:hover { transform: scale(1.08); }
.ap-plus:active { transform: scale(.92); }

.ap-dot { width:11px; height:11px; border-radius:50%; border:1px solid rgba(27,14,6,.16); flex-shrink:0; }

.ap-pill { padding:9px 16px; border:1px solid ${H.rule}; background:${H.card};
  font-size:11.5px; letter-spacing:.09em; text-transform:uppercase; color:${H.inkSoft};
  cursor:pointer; transition:border-color .18s, background .18s, color .18s; }
.ap-pill:hover { border-color:${H.gold}; }
.ap-pill.on { background:${H.ink}; border-color:${H.ink}; color:#fff; }

.ap-cta { width:100%; padding:15px 0; border:none; cursor:pointer;
  font-size:11px; letter-spacing:.24em; text-transform:uppercase; font-weight:500;
  transition:opacity .2s, background .2s, color .2s; }
.ap-cta:disabled { opacity:.4; cursor:not-allowed; }

.ap-tab { display:flex; flex-direction:column; align-items:center; gap:4px; font-size:9px;
  letter-spacing:.12em; text-transform:uppercase; cursor:pointer; color:${H.faint};
  padding:4px 10px; background:none; border:none; }
.ap-tab.on { color:${H.red}; }

.ap-nav-item { display:flex; align-items:center; gap:12px; padding:13px 18px; cursor:pointer;
  font-size:14px; font-weight:700; color:${H.ink}; background:none; width:100%;
  text-align:left; border:none; border-bottom:1px solid ${H.ruleSoft}; transition:background .15s; }
.ap-nav-item:hover { background:${H.goldSoft}; }

@media (prefers-reduced-motion: reduce) {
  .ap-tag:hover, .ap-card { animation:none !important; }
  .ap-frame img { transition:none; }
}
`;

/* ═══════════════════════════════════════════════════════════
   SIDEBAR SHAPES — same objects FurnitureStore uses
═══════════════════════════════════════════════════════════ */
const A: Record<string, React.CSSProperties> = {
  commerceCard:   { borderRadius: 18, padding: 18, background: "#fff", marginBottom: 16, boxShadow: "0 2px 16px rgba(27,14,6,.06)", border: `1.5px solid ${H.rule}` },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: H.ink },
  category:       { padding: 10, borderRadius: 12, background: H.paper, marginTop: 8, border: `1px solid ${H.rule}`, cursor: "pointer" },
  row:            { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: H.ink },
  sub:            { paddingLeft: 20, paddingTop: 8 },
  sidebar:        { background: "rgba(255,255,255,.98)", borderRadius: 20, padding: 20, boxShadow: "0 4px 24px rgba(27,14,6,.08)", position: "sticky" as const, top: 150, maxHeight: "calc(100vh - 170px)", overflowY: "auto" as const, border: `1.5px solid ${H.rule}` },
};

const S: Record<string, React.CSSProperties> = {
  grid:       { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))", gap: 16 },
  mobileGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: H.ruleSoft },
};

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const ApparelStore: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const price = (n: number) => `${cur}${((n ?? 0) * (rate ?? 1)).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const filteredListings = useSelector((s: RootState) => s.storeProducts.filteredProducts);
  const cartItems = useSelector((s: RootState) => (s as any).storeCart?.items ?? []);
  const ratingSummaries = useSelector((s: RootState) => (s as any).storeProductRatings?.summaries ?? {});
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  /* browse — mobile sheet + desktop sidebar, exactly like Furniture */
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [openCatPanel, setOpenCatPanel] = useState<string | null>(APPAREL_CATEGORIES[0]?.id ?? null);
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());

  const [activeCat, setActiveCat] = useState<string>(APPAREL_CATEGORIES[0]?.id ?? APPAREL_DEPT_ID);
  const cat = useMemo(
    () => APPAREL_CATEGORIES.find((c) => c.id === activeCat) ?? APPAREL_CATEGORIES[0],
    [activeCat]
  );
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [shotIndex, setShotIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, any>>({});
  const [wished, setWished] = useState<Set<string>>(new Set());

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const bCity = p.address?.city?.toLowerCase().trim();
      const bCountry = p.address?.country?.toLowerCase().trim();
      const bCont = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && bCity && bCity === deviceCity) tier = 0;
      else if (deviceCountry && bCountry && bCountry === deviceCountry) tier = 1;
      else if (deviceContinent && bCont && bCont === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── effects ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(listenToPublishedProducts() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllCommercePartners() as any); }, [dispatch]);
  useEffect(() => { dispatch(filterLocalProductsByCountry(null) as any); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId) as any);
    return () => { dispatch(stopUserNotificationsListener() as any); };
  }, [dispatch, userId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true);
      dispatch(filterLocalProductsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
              (results: any, status: string) => {
                if (status === "OK" && results?.[0]) {
                  const comps = results[0].address_components;
                  const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                  setUserCity(get("locality") || get("administrative_area_level_2"));
                  const country = get("country"); setUserCountry(country);
                  if (country) dispatch(filterLocalProductsByCountry(country) as any);
                }
              });
          }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          dispatch(filterLocalProductsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName })
        .then((r) => { r.onchange = () => { if (r.state === "granted") detect(); }; })
        .catch(() => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  useEffect(() => {
    if (!detailItem) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [detailItem]);

  /* ── actions ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveSub(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(next ? null : userCountry) as any);
  };

  const resetAllFilters = () => {
    setActiveSub(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery("");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalProductsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleCatSelect = (id: string) => {
    setActiveCat(id); setActiveSub(null); setActiveDeep(null);
    setOpenCatPanel(id); setOpenNodes(new Set());
  };
  const handleSubSelect = (id: string | null) => { setActiveSub((p) => (p === id ? null : id)); setActiveDeep(null); };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  const SINGLE_BRAND_CART_MODE = true;
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  const openDetail = (item: any) => { setDetailItem(item); setShotIndex(0); setQty(1); setSelectedVariants({}); };
  const closeDetail = () => setDetailItem(null);

  const handleAdd = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    if (hasVariants(item)) { openDetail(item); return; }
    if (getQty(item.listingId) >= (item.stock ?? 0)) return;
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart({
      listingId: item.listingId, name: item.name!, rate: item.rate!,
      photo: item.photos?.[0], hostId: item.hostId, brandId: item.brandId,
    } as any));
  };

  const handleDetailContinue = (toCheckout: boolean) => {
    if (!detailItem || isOutOfStock(detailItem)) return;
    const defs = variantAttrsForItem(detailItem);
    if (defs.some((a: any) => selectedVariants[String(a.id)] == null)) return;
    if (getQty(detailItem.listingId) + qty > (detailItem.stock ?? 0)) return;

    ensureCartBrandCompat(detailItem.brandId);
    dispatch(addToCart({
      listingId: detailItem.listingId, name: detailItem.name!, rate: detailItem.rate!, qty,
      photo: detailItem.photos?.[0], hostId: detailItem.hostId, brandId: detailItem.brandId,
      selectedVariants: { ...selectedVariants },
    } as any));
    closeDetail(); setQty(1); setSelectedVariants({});
    if (toCheckout) goToCheckout();
  };

  const toggleWish = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWished((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const goToCheckout = () => navigate(`/pay/${userId}`, { state: { items: cartItems } });
  const goToBrand = (id: string) => { if (id && id !== "unknown") navigate(`/brand/${id}`); };
  const handleLogout = async () => { await dispatch(logout() as any); navigate("/AuthSwitch"); };

  /* ═══════════════════════════════════
     FILTERING — always inside Apparel
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings || []).filter((l: any) => {
      if (!l.isPublished) return false;
      if (isOutOfStock(l)) return false;
      const chain = chainIdsOf(l.categoryId);
      if (!chain.includes(APPAREL_DEPT_ID)) return false;
      if (!chain.includes(activeCat)) return false;
      if (activeSub && !chain.includes(activeSub)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const attrText = detailsOf(l).map((d) => `${d.name} ${d.value}`).join(" ");
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${l.vendor ?? ""} ${(l.tags ?? []).join(" ")} ${attrText}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "newest") list = [...list].sort((a, b) => (Date.parse(b.createdAt ?? 0) || 0) - (Date.parse(a.createdAt ?? 0) || 0));

    return list;
  }, [filteredListings, activeCat, activeSub, activeDeep, searchQuery, activeSort]);

  /* group by label, nearest showroom first */
  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));
    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleProductCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const activeFilterName = activeDeep ? catName(activeDeep) : activeSub ? catName(activeSub) : null;
  const catChildren = useMemo(() => childrenOf(activeCat), [activeCat]);

  /* ═══════════════════════════════════
     MASTHEAD  (plain calls so search keeps focus)
  ═══════════════════════════════════ */
  const masthead = () => (
    <div style={{ padding: isMobile ? "12px 16px" : "16px 34px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 12 : 22 }}>
        <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="ap-hbtn" aria-label="Browse">
          <LayoutGrid size={14} />{!isMobile && <span className="ap-caps">Browse</span>}
        </button>

        <div style={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
          {searchOpen ? (
            <div className="ap-search" style={{ maxWidth: 460 }}>
              <Search size={14} color={H.faint} />
              <input autoFocus value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search the collection" aria-label="Search apparel" />
              <button onClick={() => { setSearchQuery(""); setSearchOpen(false); }} aria-label="Close search"
                style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }}>
                <X size={14} color={H.muted} />
              </button>
            </div>
          ) : (
            <span className="ap-serif" style={{ fontSize: isMobile ? 22 : 27, letterSpacing: ".14em", textTransform: "uppercase", color: H.ink }}>
              Apparel
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 12 : 18, flexShrink: 0 }}>
          {!searchOpen && (
            <button onClick={() => setSearchOpen(true)} className="ap-hbtn" aria-label="Search"><Search size={15} /></button>
          )}
          {locationDenied ? (
            <button onClick={() => setLocationModalOpen(true)} className="ap-hbtn" aria-label="Location off"><AlertTriangle size={14} /></button>
          ) : (
            <button onClick={handleToggleGlobal} className="ap-hbtn">
              {showGlobal ? <Globe size={14} /> : <MapPin size={14} />}
              {!isMobile && <span className="ap-caps">{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>}
            </button>
          )}
          {!isMobile && (
            <div style={{ position: "relative" }}>
              <button onClick={() => setProfileMenuOpen((v) => !v)} className="ap-hbtn" aria-label="Account"><User size={15} /></button>
              <AnimatePresence>
                {profileMenuOpen && (
                  <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                    style={{ position: "absolute", right: 0, top: "calc(100% + 12px)", width: 190, background: H.card, border: `1px solid ${H.rule}`, zIndex: 400, boxShadow: "0 16px 40px rgba(27,14,6,.12)" }}>
                    {[
                      { label: "Account", icon: <User size={13} />, go: () => navigate(`/profile/${userId}`) },
                      { label: "Orders", icon: <ShoppingBag size={13} />, go: () => navigate(`/clientdashboard/${userId}`) },
                      { label: "Sign out", icon: <LogOut size={13} />, go: handleLogout },
                    ].map((r, i) => (
                      <button key={r.label} onClick={r.go}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: i < 2 ? `1px solid ${H.ruleSoft}` : "none", padding: "12px 15px", cursor: "pointer", fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: r.label === "Sign out" ? H.red : H.inkSoft }}>
                        {r.icon}{r.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
          <button onClick={goToCheckout} className="ap-hbtn" style={{ position: "relative" }} aria-label="Bag">
            <ShoppingBag size={15} />
            {cartTotal > 0 && (
              <span className="ap-num" style={{ position: "absolute", top: -1, right: -9, background: H.red, color: "#fff", fontSize: 8.5, minWidth: 15, height: 15, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {cartTotal > 99 ? "99+" : cartTotal}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     🏷 HANGER RAIL
  ═══════════════════════════════════ */
  const hangerRail = () => (
    <div className="ap-rail">
      <div className="ap-rail-bar" />
      <div className="ap-tags">
        {APPAREL_CATEGORIES.map((c) => {
          const on = c.id === activeCat;
          return (
            <button key={c.id} className={`ap-tag${on ? " on" : ""}`} onClick={() => handleCatSelect(c.id)}>
              <span className="ap-tag-body">
                <span style={{ display: "flex", fontSize: 12, opacity: on ? 1 : .65 }}>{c.icon}</span>
                {c.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     EDITORIAL HEADING + SEAM NAV
  ═══════════════════════════════════ */
  const heading = () => (
    <>
      <h1 className="ap-serif" style={{ fontSize: isMobile ? 40 : 62, lineHeight: .95, color: H.ink, letterSpacing: "-.01em", marginBottom: 22 }}>
        {activeFilterName ?? cat?.name ?? "The Collection"}
      </h1>

      {catChildren.length > 0 && (
        <div className="ap-seam" style={{ marginBottom: 26 }}>
          <button className={activeSub === null ? "on" : ""} onClick={() => { setActiveSub(null); setActiveDeep(null); }}>All</button>
          {catChildren.map((c) => (
            <button key={c.id} className={activeSub === c.id ? "on" : ""} onClick={() => handleSubSelect(c.id)}>
              {c.name}
            </button>
          ))}
        </div>
      )}

      {activeSub && childrenOf(activeSub).length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 26 }}>
          {childrenOf(activeSub).map((n) => {
            const on = activeDeep === n.id;
            return (
              <button key={n.id} className={`ap-pill${on ? " on" : ""}`} onClick={() => handleDeepSelect(n.id)}>
                {on && <Check size={11} style={{ marginRight: 6, verticalAlign: "-1px" }} />}
                {n.name}
              </button>
            );
          })}
        </div>
      )}
    </>
  );

  /* ═══════════════════════════════════
     BROWSE — sidebar tree (desktop) + sheet (mobile)
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const isPicked = activeSub === node.id || activeDeep === node.id;

    return (
      <div style={depth === 0 ? A.category : { paddingLeft: 14, paddingTop: 7 }}>
        <div
          style={{ ...A.row, fontSize: depth === 0 ? 14 : 13, color: isPicked ? H.red : H.ink }}
          onClick={() => {
            if (depth === 0) handleSubSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}
        >
          <span style={{ display: "flex", color: isPicked ? H.red : H.goldDark }}>{node.icon}</span>
          {node.name}
          {kids.length > 0 && (
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}>
              <ChevronRight size={13} color={H.faint} />
            </motion.div>
          )}
          {isPicked && kids.length === 0 && <Check size={13} color={H.red} style={{ marginLeft: "auto" }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={A.sub}>
                {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const categoryTree = () => {
    const isOpen = openCatPanel === activeCat;
    return (
      <div style={A.commerceCard}>
        <div style={A.commerceHeader} onClick={() => setOpenCatPanel(isOpen ? null : activeCat)}>
          {cat?.name}
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              {catChildren.length === 0
                ? <p style={{ fontSize: 12, color: H.faint, paddingTop: 10 }}>No sub-categories here yet.</p>
                : catChildren.map((c) => <TreeNode key={c.id} node={c} depth={0} />)}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const mobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.55)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, border: `1.5px solid ${H.rule}`, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: H.rule, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${H.rule}`, padding: "12px 20px 10px" }}>
              <span className="ap-caps" style={{ color: H.red }}>Browse</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer" }}>
                <X size={15} color={H.muted} />
              </button>
            </div>
            <div style={{ padding: "10px 16px 32px" }}>
              {hangerRail()}
              {categoryTree()}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     BRAND ROW
  ═══════════════════════════════════ */
  const brandRowHeader = (brandId: string) => {
    const partner = brandMap.get(brandId);
    const shopName = partner?.name || "Label";
    const shopLogo = partner?.image || appLogo;
    const accent = brandAccentColor(partner);
    const tier = brandTier.get(brandId);
    const tierLabel = locationActive && tier !== undefined ? TIER_LABEL[tier] : null;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)} aria-label={`Open ${shopName}`}
          style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid ${H.rule}`, background: "#fff", padding: 0, cursor: "pointer" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={() => goToBrand(brandId)}
          style={{ fontWeight: 900, fontSize: 14, color: H.ink, textDecoration: "underline", textDecorationColor: accent, textDecorationThickness: 2, textUnderlineOffset: 4, cursor: "pointer" }}>
          {shopName}
        </span>
        {tierLabel && (
          <span className="ap-chip" style={{ flexShrink: 0 }}>
            <MapPin size={9} />{tierLabel}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => goToBrand(brandId)} aria-label={`Open ${shopName}`}
          style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "#fff", boxShadow: "0 3px 10px rgba(27,14,6,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={accent} />
        </motion.button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCT CARD — the plinth. Chips are whatever the
     seller saved, in the order the category defines them.
  ═══════════════════════════════════ */
  /* ═══════════════════════════════════
     PRODUCT CARD — Saloisa's editorial frame.
     Badges and chips are still whatever the seller saved.
  ═══════════════════════════════════ */
  const productCard = (item: any) => {
    const accent = brandAccentColor(brandMap.get(item.brandId));
    const q = getQty(item.listingId);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const discount = discountOf(item);
    const chips = detailsOf(item).slice(0, 2);
    const swatches = swatchesOf(item);
    const isWished = wished.has(item.listingId);
    const stock = item.stock ?? 0;
    const low = stock > 0 && stock <= LOW_STOCK_AT;
    const configurable = hasVariants(item);
    const atMax = q >= stock;

    return (
      <div key={item.listingId} className="ap-card" onClick={() => openDetail(item)}>
        <div className="ap-frame">
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name}
                onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.src = appLogo; el.style.objectFit = "contain"; el.style.opacity = ".3"; }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Shirt size={36} color={H.faint} />
              </div>}

          <div className="ap-veil" />

          {/* hover column — save + open */}
          <div className="ap-hover" style={{ position: "absolute", top: 12, right: 12, display: "flex", flexDirection: "column", gap: 8, zIndex: 10 }}
            onClick={(e) => e.stopPropagation()}>
            <button className="ap-round" onClick={(e) => toggleWish(item.listingId, e)} aria-label="Save">
              <Heart size={16} color={isWished ? accent : H.ink} fill={isWished ? accent : "none"} />
            </button>
            <button className="ap-round" onClick={(e) => { e.stopPropagation(); openDetail(item); }} aria-label="Quick view">
              <Eye size={16} color={H.ink} />
            </button>
          </div>

          {/* derived from compareAtPrice / stock only */}
          {discount !== null && (
            <span className="ap-flag" style={{ background: accent }}>−{discount}%</span>
          )}
          {discount === null && low && (
            <span className="ap-flag" style={{ background: H.ink }}>{item.stock} left</span>
          )}

          {/* copy over the photo */}
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 12px 14px", zIndex: 3 }}>
            <div className="ap-serif" style={{ fontSize: 15, fontWeight: 500, color: "#fff", lineHeight: 1.2, marginBottom: 6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }}>
              {item.name}
            </div>

            {hasRating && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
                <Star size={10} fill="#fff" color="#fff" />
                <span className="ap-num" style={{ fontSize: 10, color: "rgba(255,255,255,.82)", fontWeight: 500 }}>
                  {summary.avgRating.toFixed(1)} ({summary.totalReviews})
                </span>
              </div>
            )}

            {(swatches.length > 0 || chips.length > 0) && (
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {swatches.slice(0, 5).map((s) => (
                  <span key={s.id} className="ap-dot" title={s.name}
                    style={{ background: s.hex || H.paperWarm, borderColor: "rgba(255,255,255,.45)" }} />
                ))}
                {chips.map((c) => (
                  <span key={c.name} title={c.name}
                    style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.78)" }}>
                    {c.value}
                  </span>
                ))}
              </div>
            )}

            <span className="ap-price-tag ap-num">{price(item.rate ?? 0)}</span>
            {discount !== null && (
              <span className="ap-num" style={{ marginLeft: 8, fontSize: 11, color: "rgba(255,255,255,.6)", textDecoration: "line-through" }}>
                {price(item.compareAtPrice ?? 0)}
              </span>
            )}
          </div>
        </div>

        {/* full-width action bar */}
        <div onClick={(e) => e.stopPropagation()}>
          {q > 0 && !configurable ? (
            <div style={{ display: "flex", alignItems: "center", background: H.ink }}>
              <button aria-label="Remove one"
                onClick={() => dispatch(decrementCartItem({ listingId: item.listingId }))}
                style={{ flex: 1, height: 42, border: "none", background: "transparent", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Minus size={14} />
              </button>
              <span className="ap-num" style={{ color: "#fff", fontWeight: 700, minWidth: 28, textAlign: "center", fontSize: 13 }}>{q}</span>
              <button aria-label={atMax ? `Only ${stock} available` : "Add one"}
                onClick={(e) => handleAdd(item, e)} disabled={atMax}
                title={atMax ? `Only ${stock} in stock` : undefined}
                style={{ flex: 1, height: 42, border: "none", background: "transparent", color: "#fff", opacity: atMax ? .32 : 1, cursor: atMax ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Plus size={14} />
              </button>
            </div>
          ) : (
            <button className="ap-cart-btn" disabled={stock <= 0}
              onClick={(e) => handleAdd(item, e)}
              style={stock <= 0 ? { background: H.rule, color: H.faint, cursor: "not-allowed" } : undefined}>
              <ShoppingBag size={13} /> {stock <= 0 ? "Sold out" : configurable ? "Select options" : "Add to bag"}
            </button>
          )}
        </div>
      </div>
    );
  };


  

  /* ═══════════════════════════════════
     RESULTS
  ═══════════════════════════════════ */
  const emptyState = (title: string, body: string, cta: { label: string; onClick: () => void }) => (
    <div style={{ textAlign: "center", padding: "72px 20px" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, color: H.goldDark, animation: "apFloat 3.6s ease-in-out infinite" }}>
        <Shirt size={40} />
      </div>
      <h3 className="ap-serif" style={{ fontSize: 26, color: H.ink, marginBottom: 10 }}>{title}</h3>
      <p style={{ color: H.muted, fontSize: 13, marginBottom: 24, letterSpacing: ".02em" }}>{body}</p>
      <button onClick={cta.onClick} className="ap-caps"
        style={{ background: "none", border: "none", borderBottom: `1px solid ${H.ink}`, color: H.ink, padding: "5px 2px", cursor: "pointer" }}>
        {cta.label}
      </button>
    </div>
  );

  const results = (isMob: boolean) => {
    if (productsByBrand.length === 0) {
      if (filteredProducts.length === 0) {
        return emptyState(
          activeFilterName ? `Nothing in ${activeFilterName}` : `Nothing in ${cat?.name ?? "this category"} yet`,
          "Try another tag on the rail, or clear the filters.",
          { label: "Clear filters", onClick: resetAllFilters }
        );
      }
      if (locationDenied) {
        return emptyState(
          "Location is off",
          "Turn it on to see labels that can ship to you.",
          { label: "Enable location", onClick: () => setLocationModalOpen(true) }
        );
      }
      return emptyState(
        showGlobal ? "No global labels here" : "No local labels here",
        `Switch to ${showGlobal ? "local" : "global"} above, or clear the filters.`,
        { label: "Clear filters", onClick: resetAllFilters }
      );
    }
    return (
      <>
        {productsByBrand.map((group) => (
          <div key={group.brandId}>
            {brandRowHeader(group.brandId)}
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => productCard(item))}
            </div>
          </div>
        ))}
      </>
    );
  };

  /* ═══════════════════════════════════
     DETAIL — a full takeover. Choices only appear for
     attributes the seller saved more than one option on.
  ═══════════════════════════════════ */
  const detailView = () => {
    if (!detailItem) return null;
    const item = detailItem;
    const partner = brandMap.get(item.brandId);
    const accent = brandAccentColor(partner);
    const label = partner?.name || item.vendor || "Label";
    const shots: string[] = item.photos?.length ? item.photos : [];
    const stock = item.stock ?? 0;
    const soldOut = isOutOfStock(item);
    const discount = discountOf(item);

    const variantDefs = variantAttrsForItem(item);
    const specs = detailsOf(item);
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;
    const allChosen = variantDefs.every((a: any) => selectedVariants[String(a.id)] != null);
    const canContinue = !soldOut && qty > 0 && qty <= stock && allChosen;

    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: "fixed", inset: 0, background: H.paper, zIndex: 500, overflowY: "auto" }}>
        <div style={{ position: "sticky", top: 0, zIndex: 5, background: "rgba(251,246,238,.94)", backdropFilter: "blur(10px)", borderBottom: `1px solid ${H.rule}`, padding: isMobile ? "12px 16px" : "14px 34px", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={closeDetail} className="ap-hbtn"><ChevronLeft size={15} /><span className="ap-caps">Back</span></button>
          <div style={{ flex: 1 }} />
          <button onClick={goToCheckout} className="ap-hbtn" style={{ position: "relative" }} aria-label="Bag">
            <ShoppingBag size={15} />
            {cartTotal > 0 && (
              <span className="ap-num" style={{ position: "absolute", top: -1, right: -9, background: H.red, color: "#fff", fontSize: 8.5, minWidth: 15, height: 15, padding: "0 4px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>
            )}
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.05fr .95fr", gap: isMobile ? 0 : 46, maxWidth: 1240, margin: "0 auto", padding: isMobile ? "0 0 40px" : "34px 34px 60px" }}>
          {/* gallery */}
          <div>
            <div style={{ position: "relative", width: "100%", aspectRatio: "3/4", background: H.paperWarm, border: `1px solid ${H.ruleSoft}`, overflow: "hidden" }}>
              {shots[shotIndex]
                ? <img src={shots[shotIndex]} alt={item.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.src = appLogo; el.style.objectFit = "contain"; el.style.opacity = ".25"; }} />
                : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Shirt size={40} color={H.faint} />
                  </div>}
              {discount !== null && (
                <span className="ap-flag" style={{ background: accent, top: 14 }}>−{discount}%</span>
              )}
            </div>
            {shots.length > 1 && (
              <div style={{ display: "flex", gap: 8, marginTop: 10, overflowX: "auto", scrollbarWidth: "none" as any, padding: isMobile ? "0 16px" : 0 }}>
                {shots.map((sh, i) => (
                  <button key={i} onClick={() => setShotIndex(i)} aria-label={`View ${i + 1}`}
                    style={{ width: 58, height: 74, flexShrink: 0, padding: 0, cursor: "pointer", background: H.paperWarm, border: `1px solid ${i === shotIndex ? H.ink : H.ruleSoft}`, overflow: "hidden" }}>
                    <img src={sh} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: i === shotIndex ? 1 : .62 }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* copy */}
          <div style={{ padding: isMobile ? "26px 16px 0" : "6px 0 0", maxWidth: 520 }}>
            <button onClick={() => goToBrand(item.brandId)} className="ap-caps"
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: accent, display: "flex", alignItems: "center", gap: 5, marginBottom: 12 }}>
              {label} <ArrowUpRight size={12} />
            </button>

            <h2 className="ap-serif" style={{ fontSize: isMobile ? 30 : 40, lineHeight: 1.05, color: H.ink, marginBottom: 10 }}>{item.name}</h2>

            {item.categoryId && (
              <p className="ap-caps" style={{ color: H.faint, marginBottom: 16 }}>{catPathLabel(item.categoryId)}</p>
            )}

            <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 26, flexWrap: "wrap" }}>
              <span className="ap-serif ap-num" style={{ fontSize: 27, color: H.ink }}>{price(item.rate ?? 0)}</span>
              {discount !== null && (
                <span className="ap-serif ap-num" style={{ fontSize: 16, color: H.faint, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>
              )}
              {hasRating && (
                <span className="ap-num" style={{ fontSize: 11.5, color: H.muted, display: "flex", alignItems: "center", gap: 4 }}>
                  <Star size={11} fill={H.gold} color={H.gold} />
                  {summary.avgRating.toFixed(1)} ({summary.totalReviews.toLocaleString()})
                </span>
              )}
            </div>

            {/* choices — only the options this seller actually stocked */}
            {variantDefs.map((attr: any) => {
              const key = String(attr.id);
              const chosen = selectedVariants[key];
              const isColour = attr.inputType === "color";
              const options = savedOptionsFor(item, attr);
              return (
                <div key={key} style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 11 }}>
                    <span className="ap-caps" style={{ color: H.muted }}>{attr.name}</span>
                    {chosen == null && <span className="ap-caps" style={{ color: H.red }}>Please select</span>}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {options.map((opt: any) => {
                      const on = String(chosen) === String(opt.id);
                      if (isColour) {
                        return (
                          <button key={opt.id} onClick={() => setSelectedVariants((p) => ({ ...p, [key]: opt.id }))}
                            title={opt.name || opt.standard} aria-label={opt.name || opt.standard}
                            style={{ width: 34, height: 34, borderRadius: "50%", cursor: "pointer", padding: 0, background: opt.hex || H.paperWarm, border: on ? `1.5px solid ${H.ink}` : `1px solid ${H.rule}`, boxShadow: on ? `inset 0 0 0 3px ${H.paper}` : "none" }} />
                        );
                      }
                      return (
                        <button key={opt.id} className={`ap-pill${on ? " on" : ""}`}
                          onClick={() => setSelectedVariants((p) => ({ ...p, [key]: opt.id }))}>
                          {opt.name || opt.standard || String(opt.id)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {!soldOut && (
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
                <span className="ap-caps" style={{ color: H.muted }}>Qty</span>
                <div style={{ display: "flex", border: `1px solid ${H.rule}` }}>
                  <button onClick={() => setQty((v) => Math.max(1, v - 1))} aria-label="Decrease"
                    style={{ width: 40, height: 40, border: "none", background: H.card, cursor: "pointer", color: H.ink, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Minus size={13} />
                  </button>
                  <div className="ap-num" style={{ width: 46, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: H.ink, background: H.paperWarm }}>{qty}</div>
                  <button onClick={() => setQty((v) => Math.min(stock || 1, v + 1))} disabled={qty >= stock} aria-label="Increase"
                    style={{ width: 40, height: 40, border: "none", background: H.card, cursor: qty >= stock ? "not-allowed" : "pointer", color: H.ink, opacity: qty >= stock ? .35 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Plus size={13} />
                  </button>
                </div>
                {stock <= LOW_STOCK_AT && <span className="ap-caps" style={{ color: H.red }}>Only {stock} left</span>}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginBottom: 30 }}>
              <button className="ap-cta" disabled={!canContinue} onClick={() => handleDetailContinue(false)}
                style={{ flex: 1, background: "transparent", border: `1px solid ${canContinue ? H.ink : H.rule}`, color: canContinue ? H.ink : H.faint }}>
                {soldOut ? "Sold out" : "Add to bag"}
              </button>
              <button className="ap-cta" disabled={!canContinue} onClick={() => handleDetailContinue(true)}
                style={{ flex: 1, background: canContinue ? accent : H.rule, color: canContinue ? "#fff" : H.faint }}>
                Buy now
              </button>
            </div>

            {item.description && (
              <div style={{ fontSize: 13.5, color: H.inkSoft, lineHeight: 1.85, letterSpacing: ".01em", marginBottom: 26 }}
                dangerouslySetInnerHTML={{ __html: item.description }} />
            )}

            {specs.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div className="ap-caps" style={{ color: H.faint, marginBottom: 9 }}>Details</div>
                <dl className="ap-spec">
                  {specs.map((d) => (
                    <React.Fragment key={d.name}>
                      <dt>{d.name}</dt>
                      <dd>{d.value}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            )}

            {item.vendor && (
              <p style={{ fontSize: 11.5, color: H.muted, marginBottom: 10 }}>
                <strong style={{ color: H.ink }}>Vendor:</strong> {item.vendor}
              </p>
            )}

            {item.tags?.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 12 }}>
                {item.tags.map((t: string) => (
                  <span key={t} className="ap-chip">#{t}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const locationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <motion.div initial={{ y: 18, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 18, opacity: 0 }} onClick={(e) => e.stopPropagation()}
            style={{ width: 330, background: H.card, border: `1px solid ${H.rule}`, padding: 28, textAlign: "center" }}>
            <MapPin size={20} color={H.red} style={{ marginBottom: 14 }} />
            <h3 className="ap-serif" style={{ fontSize: 22, color: H.ink, marginBottom: 10 }}>Location is off</h3>
            <p style={{ color: H.muted, fontSize: 12.5, lineHeight: 1.7, marginBottom: 22 }}>
              Allow location in your browser settings to see labels that can ship to you.
            </p>
            <button onClick={() => setLocationModalOpen(false)} className="ap-cta"
              style={{ background: H.ink, color: "#fff" }}>Understood</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const bottomTabs = () => (isMobile && userId ? (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: 60, background: "rgba(251,246,238,.96)", backdropFilter: "blur(12px)", borderTop: `1px solid ${H.rule}`, display: "flex", justifyContent: "space-around", alignItems: "center", zIndex: 150 }}>
      {[
        { icon: <Home size={19} />, label: "Home", on: false, go: () => navigate(`/StoreProductsHome/${userId}`) },
        { icon: <Shirt size={19} />, label: "Apparel", on: true, go: () => navigate("/ApparelStore") },
        {
          label: "Bag", on: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingBag size={19} />
              {cartTotal > 0 && (
                <span className="ap-num" style={{ position: "absolute", top: -5, right: -7, background: H.red, color: "#fff", fontSize: 8, minWidth: 14, height: 14, padding: "0 3px", borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>{cartTotal}</span>
              )}
            </div>
          ), go: goToCheckout,
        },
        { icon: <Package size={19} />, label: "Orders", on: false, go: () => navigate(`/clientdashboard/${userId}`) },
        { icon: <User size={19} />, label: "Account", on: false, go: () => navigate(`/profile/${userId}`) },
      ].map((t) => (
        <button key={t.label} onClick={t.go} className={`ap-tab${t.on ? " on" : ""}`}>
          {React.cloneElement(t.icon as any, { color: t.on ? H.red : H.faint })}
          <span>{t.label}</span>
        </button>
      ))}
    </div>
  ) : null);

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div className="ap">
      <style>{GLOBAL_CSS}</style>

      <header className="ap-top">
        {masthead()}
        <div style={{ padding: isMobile ? "0 16px" : "0 34px" }}>{hangerRail()}</div>
      </header>

      {mobileCategorySheet()}

      {!isMobile && (
        <div style={{ display: "grid", gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr", gap: 24, maxWidth: 1440, margin: "0 auto", padding: "38px 34px 0", alignItems: "start" }}>
          {desktopCatOpen && <aside style={A.sidebar}>{categoryTree()}</aside>}
          <main style={{ minWidth: 0 }}>
            {heading()}
            {results(false)}
          </main>
        </div>
      )}

      {isMobile && (
        <div style={{ padding: "24px 16px 0" }}>
          {heading()}
          {results(true)}
        </div>
      )}

      <AnimatePresence>{detailItem && detailView()}</AnimatePresence>
      {locationModal()}
     
    </div>
  );
};

export default ApparelStore;